import { FreestyleRoom, FreestyleScore } from './types';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const makeUrl = (path: string) => {
    if (isLocal) return `/api/fs/rooms${path}`;
    return `./api.php/fs/rooms${path}?action=fs_rooms`;
};

const makeFreshUrl = (path: string) => {
    const url = new URL(makeUrl(path), window.location.href);
    url.searchParams.set('_ts', Date.now().toString());
    return url.toString();
};

const createApiError = (message: string, status?: number) => {
    const error = new Error(message) as Error & { status?: number };
    if (typeof status === 'number') {
        error.status = status;
    }
    return error;
};

export const fetchRooms = async () => {
    const res = await fetch(makeFreshUrl(''), { credentials: 'include', cache: 'no-store' });
    if (!res.ok) throw new Error('Failed to fetch rooms');
    return res.json();
};

export const createRoom = async (name: string, password?: string) => {
    const res = await fetch(makeUrl(''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, password }),
        credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to create room');
    return res.json();
};

export const joinRoom = async (roomId: string, password?: string) => {
    const res = await fetch(makeUrl(`/${roomId}/join`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
        credentials: 'include'
    });
    if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to join room');
    }
    return res.json();
};

export const fetchRoom = async (roomId: string) => {
    const res = await fetch(makeFreshUrl(`/${roomId}`), { credentials: 'include', cache: 'no-store' });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw createApiError(data.error || 'Failed to fetch room', res.status);
    }
    return res.json();
};

export const updateRoom = async (roomId: string, room: Partial<FreestyleRoom>) => {
    const res = await fetch(makeUrl(`/${roomId}/update`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room }),
        credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to update room');
    return res.json();
};

export const submitScore = async (roomId: string, competitorId: string, score: FreestyleScore) => {
    const res = await fetch(makeUrl(`/${roomId}/score`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ competitorId, score }),
        credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to submit score');
    return res.json();
};

export const submitScoresBatch = async (
    roomId: string,
    scores: Array<{ competitorId: string; score: FreestyleScore }>
) => {
    const res = await fetch(makeUrl(`/${roomId}/score-batch`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ scores }),
        credentials: 'include'
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit scores');
    }
    return res.json();
};

export const calculatePrelimResults = async (roomId: string) => {
    const res = await fetch(makeUrl(`/${roomId}/calculate-prelims`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to calculate prelim results');
    }
    return res.json();
};

export const submitVote = async (roomId: string, matchId: string, votedCompetitorId: string) => {
    const res = await fetch(makeUrl(`/${roomId}/vote`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ matchId, votedCompetitorId }),
        credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to submit vote');
    return res.json();
};

export const deleteRoom = async (roomId: string) => {
    const res = await fetch(makeUrl(`/${roomId}/delete`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to delete room');
    return res.json();
};
